import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { PropertyList, PropertyListItem, SignedNumber } from '@dndbeyond/character-common-components/es';
import { Collapsible, ProficiencyLevelIcon } from '@dndbeyond/character-components/es';
import {
    rulesEngineSelectors,
    characterActions,
    RuleData,
    RuleDataUtils,
    Skill,
    SkillUtils,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../actions/sidebar';
import CustomizeDataEditor from '../../../components/CustomizeDataEditor';
import EditorBox from '../../../components/EditorBox';
import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeader } from '../../../components/Sidebar';
import { RemoveButton } from '../../../components/common/Button';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum } from '../../../utils';
import { PaneIdentifiersCustomSkill } from '../../../utils/PaneIdentifier/typings';

interface Props extends DispatchProp {
    customSkills: Array<Skill>;
    ruleData: RuleData;
    identifiers: PaneIdentifiersCustomSkill | null;
    isReadonly: boolean;
    theme: CharacterTheme;
}
interface State {
    skill: Skill | null;
}
class CustomSkillPane extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = this.generateStateData(props);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { customSkills, identifiers } = this.props;

        if (customSkills !== prevProps.customSkills || identifiers !== prevProps.identifiers) {
            this.setState(this.generateStateData(this.props));
        }
    }

    generateStateData = (props: Props): State => {
        const { customSkills, identifiers } = props;

        let foundSkill: Skill | null | undefined = null;
        if (identifiers !== null) {
            foundSkill = customSkills.find((skill) => identifiers.id === skill.id);
        }

        return {
            skill: foundSkill ? foundSkill : null,
        };
    };

    handleSkillsManageShow = (): void => {
        const { dispatch } = this.props;

        dispatch(sidebarActions.paneHistoryPush(PaneComponentEnum.SKILLS));
    };

    handleSkillRemove = (): void => {
        const { skill } = this.state;
        const { dispatch } = this.props;

        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.SKILLS));
        if (skill) {
            dispatch(characterActions.customProficiencyRemove(skill.id));
        }
    };

    getData = (): Record<string, any> => {
        const { skill } = this.state;

        if (skill === null) {
            return {};
        }

        const originalContract = SkillUtils.getOriginalContract(skill);

        if (originalContract === null) {
            return {};
        }

        const { name, notes, proficiencyLevel, statId, type, override, miscBonus, magicBonus, description } =
            originalContract;

        return {
            name,
            notes,
            proficiencyLevel,
            statId,
            type,
            override,
            miscBonus,
            magicBonus,
            description,
        };
    };

    handleDataUpdate = (data: Record<string, any>): void => {
        const { skill } = this.state;
        const { dispatch } = this.props;

        if (skill) {
            dispatch(characterActions.customProficiencySet(skill.id, data));
        }
    };

    renderCustomize = (): React.ReactNode => {
        const { ruleData, isReadonly } = this.props;

        if (isReadonly) {
            return null;
        }

        return (
            <Collapsible layoutType={'minimal'} header="Edit" className="ct-custom-skill-pane__edit">
                <EditorBox>
                    <CustomizeDataEditor
                        data={this.getData()}
                        enableName={true}
                        enableNotes={true}
                        enableDescription={true}
                        enableStat={true}
                        enableProficiencyLevel={true}
                        enableMagicBonus={true}
                        enableMiscBonus={true}
                        enableOverride={true}
                        maxNameLength={1024}
                        onDataUpdate={this.handleDataUpdate}
                        statOptions={RuleDataUtils.getStatOptions(ruleData)}
                        proficiencyLevelOptions={RuleDataUtils.getProficiencyLevelOptions(ruleData)}
                    />
                </EditorBox>
            </Collapsible>
        );
    };

    render() {
        const { skill } = this.state;
        const { isReadonly, ruleData, theme } = this.props;

        if (skill === null) {
            return <PaneInitFailureContent />;
        }
        const notes = SkillUtils.getNotes(skill);
        const description = SkillUtils.getDescription(skill);
        const modifier = SkillUtils.getModifier(skill);
        const statName = RuleDataUtils.getAbilityShortName(SkillUtils.getStat(skill), ruleData);
        const proficiencyLevel = SkillUtils.getProficiencyLevel(skill);
        const name = SkillUtils.getName(skill);

        return (
            <div className="ct-custom-skill-pane" key={skill.id}>
                <SidebarHeader parent="Skills" onParentClick={this.handleSkillsManageShow}>
                    <div className="ct-custom-skill-pane__header">
                        <div className="ct-custom-skill-pane__header-icon">
                            <ProficiencyLevelIcon theme={theme} proficiencyLevel={proficiencyLevel} />
                        </div>
                        <div className="ct-custom-skill-pane__header-ability">
                            {statName === null ? '--' : statName}
                        </div>
                        <div className="ct-custom-skill-pane__header-name">{name}</div>
                        <div className="ct-custom-skill-pane__header-modifier">
                            {modifier === null ? '--' : <SignedNumber number={modifier} />}
                        </div>
                    </div>
                </SidebarHeader>
                {this.renderCustomize()}
                {notes && (
                    <PropertyList className="ct-custom-skill-pane__properties">
                        <PropertyListItem label="Notes">{notes}</PropertyListItem>
                    </PropertyList>
                )}
                {description && <div className="ct-custom-skill-pane__description">{description}</div>}
                {!isReadonly && (
                    <div className="ct-custom-skill-pane__actions">
                        <RemoveButton onClick={this.handleSkillRemove}>Remove</RemoveButton>
                    </div>
                )}
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        ruleData: rulesEngineSelectors.getRuleData(state),
        customSkills: rulesEngineSelectors.getCustomSkills(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
    };
}

export default connect(mapStateToProps)(CustomSkillPane);
